/**
 * Audit Trail Page
 * FDA 21 CFR Part 11 compliant audit log viewer
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  History,
  Search,
  User,
  FileText,
  Edit3,
  LogIn,
  LogOut,
  Key,
  UserPlus,
  AlertTriangle,
  MessageSquare,
} from 'lucide-react';

interface AuditChange {
  fieldName: string;
  fieldLabel?: string;
  oldValue: unknown;
  newValue: unknown;
}

interface AuditEntry {
  _id: string;
  auditId: string;
  timestamp: string;
  action: string;
  entityType: string;
  entityId?: string;
  userId: string;
  username: string;
  userFullName: string;
  userRole: string;
  studyId?: string;
  studyName?: string;
  subjectDisplayId?: string;
  formName?: string;
  changes?: AuditChange[];
  reason?: string;
  ipAddress?: string;
}

const AuditTrail: React.FC = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('7days');

  // Fetch audit entries
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', actionFilter, dateFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.set('action', actionFilter);

      // Date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate: Date;
        if (dateFilter === 'today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (dateFilter === '7days') {
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else {
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        }
        params.set('startDate', startDate.toISOString());
      }

      params.set('limit', '200');
      const res = await fetch(`/api/audit?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load audit trail');
      return res.json();
    },
  });

  const entries: AuditEntry[] = data?.records || [];

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.username?.toLowerCase().includes(search) ||
      entry.userFullName?.toLowerCase().includes(search) ||
      entry.action?.toLowerCase().includes(search) ||
      entry.entityType?.toLowerCase().includes(search) ||
      entry.subjectDisplayId?.toLowerCase().includes(search) ||
      entry.formName?.toLowerCase().includes(search)
    );
  });

  const getActionIcon = (action: string) => {
    if (action.includes('LOGIN')) return <LogIn className="w-4 h-4" />;
    if (action.includes('LOGOUT')) return <LogOut className="w-4 h-4" />;
    if (action.includes('PASSWORD')) return <Key className="w-4 h-4" />;
    if (action.includes('USER')) return <UserPlus className="w-4 h-4" />;
    if (action.includes('QUERY')) return <MessageSquare className="w-4 h-4" />;
    if (action.includes('UPDATE') || action.includes('CREATE')) return <Edit3 className="w-4 h-4" />;
    if (action.includes('SIGN')) return <FileText className="w-4 h-4" />;
    if (action.includes('UPLOAD')) return <FileText className="w-4 h-4" />;
    return <History className="w-4 h-4" />;
  };

  const getActionColor = (action: string) => {
    if (action.includes('FAILED')) return 'bg-red-100 text-red-700';
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'bg-blue-100 text-blue-700';
    if (action.includes('CREATE')) return 'bg-green-100 text-green-700';
    if (action.includes('UPDATE') || action.includes('PASSWORD')) return 'bg-amber-100 text-amber-700';
    if (action.includes('DELETE') || action.includes('DEACTIVATE')) return 'bg-red-100 text-red-700';
    if (action.includes('SIGN')) return 'bg-purple-100 text-purple-700';
    if (action.includes('QUERY')) return 'bg-indigo-100 text-indigo-700';
    if (action.includes('UPLOAD') || action.includes('EXPORT')) return 'bg-teal-100 text-teal-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-700 font-medium">Error loading audit trail</h2>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
        <p className="text-gray-600">FDA 21 CFR Part 11 compliant system activity log</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user, action, or record..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
              />
            </div>
          </div>

          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
          >
            <option value="all">All Actions</option>
            <option value="CREATE">Creates</option>
            <option value="UPDATE">Updates</option>
            <option value="LOGIN">Login</option>
            <option value="LOGOUT">Logout</option>
            <option value="LOGIN_FAILED">Failed Logins</option>
            <option value="SIGN">Signatures</option>
            <option value="QUERY_OPEN">Query Opened</option>
            <option value="QUERY_RESPOND">Query Responded</option>
            <option value="QUERY_CLOSE">Query Closed</option>
            <option value="PASSWORD_CHANGE">Password Changes</option>
            <option value="UPLOAD">File Uploads</option>
            <option value="EXPORT">Exports</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-opci-navy-500 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading audit trail...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="p-8 text-center">
            <History className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No audit entries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Record</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredEntries.map((entry) => (
                  <tr key={entry._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(entry.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{entry.userFullName || entry.username}</p>
                          <p className="text-xs text-gray-500">{entry.userRole}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getActionColor(entry.action)}`}>
                        {getActionIcon(entry.action)}
                        {entry.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-600">
                        <span className="font-medium">{entry.entityType}</span>
                        {entry.subjectDisplayId && (
                          <span className="ml-1 text-xs text-gray-500">({entry.subjectDisplayId})</span>
                        )}
                        {entry.formName && (
                          <span className="block text-xs text-gray-400">{entry.formName}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs">
                      {entry.reason && (
                        <p className="text-gray-600 italic mb-1">Reason: {entry.reason}</p>
                      )}
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="space-y-0.5">
                          {entry.changes.slice(0, 3).map((c, i) => (
                            <div key={i} className="text-xs">
                              <span className="font-medium">{c.fieldLabel || c.fieldName}</span>
                              {c.oldValue != null && (
                                <span className="text-red-500 line-through ml-1">{String(c.oldValue)}</span>
                              )}
                              {c.newValue != null && (
                                <span className="text-green-600 ml-1">{String(c.newValue)}</span>
                              )}
                            </div>
                          ))}
                          {entry.changes.length > 3 && (
                            <span className="text-xs text-gray-400">+{entry.changes.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Compliance Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">FDA 21 CFR Part 11 Compliance</h3>
            <p className="text-blue-700 text-sm mt-1">
              This audit trail maintains a complete, unalterable record of all system activities.
              Entries cannot be modified or deleted to ensure regulatory compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
